"use client";

import { PieChart } from "@/components/charts/pie-chart";
import { PieSlice } from "@/components/charts/pie-slice";
import { PieCenter } from "@/components/charts/pie-center";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import {
  Legend,
  LegendItem,
  LegendLabel,
  LegendMarker,
  LegendValue,
  useLegendItem,
} from "@/components/charts/legend";
import { useLegendClickSync } from "@/components/charts/use-legend-click-sync";
import { ChartLegendHoverProvider } from "@/components/charts/chart-legend-hover";
import { cn } from "@/lib/utils";
import { ChartDataPoint } from "@/types/modules";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { motion } from "motion/react";
import React, { JSX, useMemo, useState } from "react";
import { CHART_SQUARE_SIZE } from "@/config/layout";
import { accentChartColors } from "../module-accent";
import { chartPaletteStyle } from "../imports-token-scope";

const CHART_SIZE = CHART_SQUARE_SIZE.pie;

export default function ModulePieChart({
  data,
  grayscale,
  accent,
  className,
}: {
  data?: ChartDataPoint[];
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const {
    activeIndex,
    setLabelRef,
    handleSelect,
    selectedOrigin,
    legendSelectedIndex,
    elementIndex,
    toggleElementIndex,
  } = useLegendClickSync();
  const [chartHovered, setChartHovered] = useState<number | null>(null);
  const palette = accentChartColors(grayscale, accent);

  const valueKey = React.useMemo(() => {
    if (!data || data.length === 0) return "value";
    const firstItem = data[0];
    if (!firstItem) return "value";
    const keys = Object.keys(firstItem).filter((key) => key !== "name" && key !== "color");
    return keys[0] ?? "value";
  }, [data]);

  const pieData = useMemo(
    () =>
      (data ?? []).map((item, i) => ({
        label: stripInlineMarkdown(String(item.name)),
        value:
          typeof item[valueKey] === "number"
            ? Number(item[valueKey])
            : Number(item[valueKey]) || 0,
        color:
          typeof item.color === "string" && item.color
            ? item.color
            : palette[i % palette.length]!,
      })),
    [data, valueKey, palette],
  );

  const total = useMemo(
    () => pieData.reduce((sum, d) => sum + d.value, 0),
    [pieData],
  );

  const legendItems = useMemo(
    () =>
      pieData.map((item) => ({
        label: item.label,
        value: item.value,
        maxValue: total,
        color: item.color,
      })),
    [pieData, total],
  );

  return (
    <ChartLegendHoverProvider
      hoveredIndex={chartHovered}
      onHoverChange={setChartHovered}
      selectedIndex={activeIndex}
      selectedOrigin={selectedOrigin}
      elementIndex={elementIndex}
      onElementToggle={toggleElementIndex}
    >
    <div className={cn("flex w-full flex-col gap-3", className)} style={chartPaletteStyle(grayscale, accent)}>
      <div className="flex w-full items-center justify-center">
        <div style={{ width: CHART_SIZE, height: CHART_SIZE }}>
          <PieChart
            data={pieData}
            innerRadius={90}
            padAngle={0.02}
            cornerRadius={2}
            hoveredIndex={chartHovered ?? elementIndex ?? activeIndex}
            onHoverChange={setChartHovered}
          >
            {pieData.map((_, i) => (
              <PieSlice key={i} index={i} />
            ))}
            <PieCenter />
          </PieChart>
        </div>
      </div>

      <SwipeScroll className="w-full pb-1" fade="auto">
        <Legend
          items={legendItems}
          hoveredIndex={chartHovered}
          onHoverChange={setChartHovered}
          selectedIndex={legendSelectedIndex}
          onSelect={handleSelect}
          onLabelRef={setLabelRef}
          className="flex flex-row gap-6"
        >
          <PieLegendItem setRef={setLabelRef} />
        </Legend>
      </SwipeScroll>
    </div>
    </ChartLegendHoverProvider>
  );
}

function PieLegendItem({
  setRef,
}: {
  setRef: (i: number) => (el: HTMLElement | null) => void;
}) {
  const ctx = useLegendItem();
  return (
    <LegendItem className="flex shrink-0 items-start gap-2">
      <LegendMarker className="mt-0.5 h-2.5 w-2.5" />
      <motion.div
        ref={setRef(ctx.index)}
        data-module-type="pie"
        data-part-index={ctx.index}
        data-part-label={ctx.item.label}
        className="flex flex-col gap-0.5"
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22, delay: ctx.index * 0.03 }}
      >
        <LegendLabel />
        <LegendValue className="text-[11px] text-neutral-500 dark:text-neutral-400" />
      </motion.div>
    </LegendItem>
  );
}
